/**
 * Saisie à la main des chiffres du dashboard consolidé.
 *
 * ⚠️ **Le back-office n'est plus forcément saisi.** Une boutique PrestaShop connectée alimente
 * les mêmes deux lignes automatiquement ; ce qu'on tape ici **l'emporte** sur le relevé, parce
 * qu'un chiffre tapé à la main l'a souvent été précisément parce que l'automatique était faux.
 * Les objectifs, eux, n'ont pas d'équivalent connecté.
 *
 * ## Quatre domaines, deux modes — et l'ordre compte
 *
 * La modale s'organise par **domaine** : le réalisé du back-office, les objectifs que le client
 * s'est fixés, les coûts qu'aucune régie ne rend, et les budgets qu'il a prévus. Les quatre
 * proposent ensuite les deux mêmes façons d'entrer les chiffres : à la main, ou par fichier.
 *
 * Les deux derniers se saisissent **destination par destination**. À la main, la destination se
 * choisit une fois au-dessus de la grille ; dans un fichier, elle est une colonne — sans quoi
 * il faudrait un import par levier. C'est la seule différence entre les quatre formats.
 *
 * Elle a d'abord été bâtie dans l'autre sens — « Saisie », « Import », puis « Coûts » ajouté à
 * côté. Les trois boutons mélangeaient alors deux axes : deux modes et un domaine, sur la même
 * ligne, sans que rien ne dise lequel était lequel. Séparer les deux niveaux rend le choix
 * lisible et laisse la place aux coûts à venir — une régie, une prestation.
 *
 * ## Le piège de l'effacement croisé
 *
 * Back-office et objectifs partagent **une même ligne en base**, et la route a une sémantique
 * PUT : la ligne envoyée est l'état complet de la période. Enregistrer les objectifs seuls
 * effacerait donc le back-office du même mois, sans un mot. `completer` recolle les colonnes de
 * l'autre domaine avant l'envoi — c'est la contrepartie de la séparation, et elle n'est pas
 * facultative.
 *
 * **Le serveur dit ce qu'il a réellement écrit**, et la modale ne se referme que là-dessus : un
 * « enregistré » affiché sur la foi d'un envoi parti laisserait croire à une sauvegarde qu'une
 * validation a pu refuser en silence.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Loader, PencilLine, Upload, Target, Store, Euro, Wallet } from 'lucide-react';
import config from '../../config';
import GrilleDeSaisie from './GrilleDeSaisie';
import SaisiesImport from './SaisiesImport';
import OngletMontantsParCible from './OngletMontantsParCible';

const API_URL = config.API_URL;

/**
 * Les trois domaines, et ce qui les distingue.
 *
 * `champs` pilote la grille, `jeu` pilote l'import, `cles` sert à recoller les colonnes de
 * l'autre domaine avant l'envoi. Ajouter un domaine tiendra dans cette table.
 */
const DOMAINES = [
  {
    cle: 'backOffice',
    titre: 'Back-office',
    icone: <Store size={15} />,
    aide: "Chiffres relevés par le client dans son back-office : ce qu'il encaisse réellement, "
      + 'et non ce que la solution de mesure lui attribue.',
    champs: [
      { cle: 'boTransactions', titre: 'Transactions BO', entier: true },
      { cle: 'boRevenue', titre: 'CA BO (€)', entier: false }
    ]
  },
  {
    cle: 'objectifs',
    titre: 'Objectifs',
    icone: <Target size={15} />,
    aide: "Objectifs que le client s'est fixés. Le tableau les compare au réalisé et affiche "
      + "l'écart et le taux de réalisation.",
    champs: [
      { cle: 'objRevenue', titre: 'Objectif CA (€)', entier: false },
      { cle: 'objTransactions', titre: 'Objectif transactions', entier: true }
    ]
  },
  {
    cle: 'couts',
    titre: 'Coûts',
    icone: <Euro size={15} />,
    aide: "Coûts que les API ne rendent pas. Sur un canal gratuit — agence SEO, outil "
      + "d'emailing — ils sont les seuls, et rendent le COS et le CPA calculables là où ils "
      + "n'existaient pas. Sur une régie, ils s'ajoutent à l'achat d'espace : abonnement à la "
      + 'plateforme, frais de gestion, minimum mensuel.'
  },
  {
    cle: 'budget',
    titre: 'Budgété',
    icone: <Wallet size={15} />,
    aide: 'Budgets prévisionnels, destination par destination : ce qui était prévu, à comparer '
      + "à ce qui a été dépensé. Un budget se saisit comme un coût, mais ne s'écrit pas au même "
      + "endroit — sans quoi enregistrer l'un effacerait l'autre sur la même période."
  }
];

/**
 * Les textes d'aide de la grille, écrits ici plutôt que dans le composant.
 *
 * Le catalogue des destinations est commun aux coûts et aux budgets, mais son texte d'aide
 * parle de coût — c'est celui d'un canal, pas celui d'un domaine. Le reprendre tel quel sous
 * l'onglet Budgété annoncerait un COS calculable là où on saisit une prévision.
 */
const aideCout = (cible) => cible?.aide
  || `Le coût saisi s'ajoute au bloc « ${cible?.titre || ''} » du tableau et y rend le COS et le CPA calculables. Une période sans coût saisi reste vide, jamais à zéro.`;

const aideBudget = (cible) => `Budget prévu pour le bloc « ${cible?.titre || ''} ». Il ne modifie aucun chiffre du tableau : c'est une prévision, à comparer au réalisé. Une période sans budget saisi reste vide, jamais à zéro.`;

/** Les quatre colonnes que back-office et objectifs se partagent dans une même ligne. */
const COLONNES_PARTAGEES = ['boTransactions', 'boRevenue', 'objRevenue', 'objTransactions'];

function SaisiesModal({ ouverte, onFermer, onEnregistre, token, proprieteId, nomMesure }) {
  const [domaine, setDomaine] = useState('backOffice');
  const [mode, setMode] = useState('saisie');
  const [saisies, setSaisies] = useState([]);
  const [couts, setCouts] = useState([]);
  const [budgets, setBudgets] = useState([]);
  /**
   * Destinations de coût, **envoyées par le serveur**.
   *
   * L'écran ne tient pas sa propre liste de blocs : elle divergerait au premier canal ajouté, et
   * un coût pourrait alors viser une destination que le tableau ne construit plus.
   */
  const [ciblesDeCout, setCiblesDeCout] = useState([]);
  /**
   * En chargement dès le premier rendu, et non après.
   *
   * Un effet s'exécute **après** le rendu qui le déclare : partir de `false` affichait une grille
   * vide le temps d'une image, puis le voile de chargement, puis les données — la modale semblait
   * se recharger toute seule à l'ouverture.
   */
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const domaineActuel = useMemo(
    () => DOMAINES.find(d => d.cle === domaine) || DOMAINES[0],
    [domaine]
  );

  const charger = useCallback(async () => {
    if (!proprieteId) return;
    setChargement(true);
    setErreur('');
    try {
      // Les deux jeux en parallèle : l'un n'attend rien de l'autre, et la modale ne doit
      // s'ouvrir qu'une fois, pas se remplir en deux temps.
      const [r, rc, rb] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/consolide/saisies`, {
          headers, params: { propertyId: proprieteId }
        }),
        axios.get(`${API_URL}/api/dashboard/consolide/couts`, {
          headers, params: { propertyId: proprieteId }
        }),
        axios.get(`${API_URL}/api/dashboard/consolide/budgets`, {
          headers, params: { propertyId: proprieteId }
        })
      ]);
      setSaisies(r.data.saisies || []);
      setCouts(rc.data.couts || []);
      setBudgets(rb.data.budgets || []);
      // Les deux routes servent le même catalogue : celle des coûts fait foi, l'autre ne
      // servirait qu'à le contredire un jour.
      setCiblesDeCout(rc.data.cibles || []);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Impossible de lire les saisies');
    } finally {
      setChargement(false);
    }
  }, [proprieteId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ouverte) return;
    // Repartir en chargement à chaque ouverture : les chiffres ont pu changer ailleurs, et
    // montrer les précédents comme s'ils étaient à jour serait pire que de faire patienter.
    setChargement(true);
    charger();
  }, [ouverte, charger]);

  // Changer de domaine remet le mode sur la saisie : c'est le geste le plus courant, et garder
  // l'écran d'import d'un domaine sur un autre déroute plus qu'il ne fait gagner.
  useEffect(() => { setMode('saisie'); setErreur(''); }, [domaine]);

  /**
   * Recolle les colonnes de l'autre domaine avant l'envoi.
   *
   * Sans cela, enregistrer deux objectifs pour septembre effacerait le back-office de septembre,
   * la route considérant la ligne reçue comme l'état complet de la période.
   */
  const completer = useCallback((lignes) => {
    const parCle = new Map(saisies.map(s => [`${s.granularite}|${s.debut}`, s]));
    return lignes.map(ligne => {
      const existante = parCle.get(`${ligne.granularite}|${ligne.debut}`) || {};
      const complete = { ...ligne };
      for (const cle of COLONNES_PARTAGEES) {
        if (!(cle in complete)) complete[cle] = existante[cle] ?? null;
      }
      return complete;
    });
  }, [saisies]);

  /**
   * Envoie les périodes touchées, puis recharge.
   *
   * Le rechargement n'est pas une précaution de confort : le serveur aligne les bornes et peut
   * avoir supprimé des périodes vidées. Garder l'état local ferait diverger l'écran de ce qui est
   * réellement en base, et la divergence ne se verrait qu'à la réouverture.
   */
  const envoyer = async (chemin, corps, auSucces) => {
    setEnregistrement(true);
    setErreur('');
    try {
      const r = await axios.put(`${API_URL}/api/dashboard/consolide/${chemin}`,
        { propertyId: proprieteId, ...corps }, { headers });

      if (!r.data?.success) throw new Error(r.data?.error || 'Enregistrement refusé');

      if (auSucces) auSucces();
      await charger();
      // Les chiffres du tableau changent : le laisser en l'état afficherait une répartition
      // périmée, sans que rien ne le signale.
      if (onEnregistre) onEnregistre();
    } catch (e) {
      const donnees = e.response?.data;
      const detail = Array.isArray(donnees?.fautes) && donnees.fautes.length > 0
        ? ` — ${donnees.fautes.slice(0, 3).map(f => f.message).join(' ; ')}`
        : '';
      setErreur(`${donnees?.error || e.message || "Erreur lors de l'enregistrement"}${detail}`);
    } finally {
      setEnregistrement(false);
    }
  };

  const enregistrerSaisies = (lignes, auSucces) => envoyer('saisies', { saisies: completer(lignes) }, auSucces);
  const enregistrerCouts = (lignes, auSucces) => envoyer('couts', { couts: lignes }, auSucces);
  const enregistrerBudgets = (lignes, auSucces) => envoyer('budgets', { budgets: lignes }, auSucces);

  if (!ouverte) return null;

  const onglet = (actif, cle, icone, titre, onClick, principal) => (
    <button
      key={cle}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: principal ? '8px 16px' : '6px 12px',
        borderRadius: '8px', fontSize: principal ? '0.875rem' : '0.8125rem',
        cursor: 'pointer', fontWeight: principal && actif ? 600 : 400,
        border: '1px solid ' + (actif ? '#7c2d12' : '#e5e7eb'),
        background: actif ? '#7c2d12' : '#fff',
        color: actif ? '#fff' : '#374151'
      }}
    >
      {icone} {titre}
    </button>
  );

  return ReactDOM.createPortal(
    <div
      onClick={onFermer}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: '14px', padding: '22px',
          width: '92%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '1.0625rem', color: '#1f2937' }}>Données saisies manuellement</h3>
          <button onClick={onFermer} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 0 }}>
            <X size={20} />
          </button>
        </div>

        {/* Premier niveau : le domaine. C'est le choix qui décide de ce qu'on saisit. */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {DOMAINES.map(d => onglet(domaine === d.cle, d.cle, d.icone, d.titre, () => setDomaine(d.cle), true))}
        </div>

        <p style={{ margin: '0 0 14px', fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.5 }}>
          {domaineActuel.aide}
          {' '}
          Une valeur saisie pour un mois ou une semaine est répartie à parts égales sur ses jours.
          {' '}
          {/*
            La promesse « le tableau la reconstitue à toutes les échelles » ne vaut que pour ce
            que le tableau lit. Aucun écran n'affiche encore les budgets : l'annoncer ici ferait
            chercher longtemps une colonne qui n'existe pas.
          */}
          {domaine === 'budget'
            ? 'Elle est conservée telle quelle, prête à être comparée au réalisé.'
            : 'Le tableau peut ainsi la reconstituer à toutes les échelles.'}
          {' '}
          Là où une période plus fine est saisie, c'est elle qui vaut.
          {domaine === 'backOffice' && nomMesure ? ` Le CA est comparé à celui mesuré par ${nomMesure}.` : ''}
        </p>

        {/* Second niveau : le mode. Les quatre domaines acceptent un fichier. */}
        {(
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {onglet(mode === 'saisie', 'saisie', <PencilLine size={14} />, 'Saisie', () => setMode('saisie'), false)}
            {onglet(mode === 'import', 'import', <Upload size={14} />, 'Import de fichier', () => setMode('import'), false)}
          </div>
        )}

        {/*
          Coûts et budgets rendent le même composant : une clé par domaine, sans quoi React
          garderait la même instance d'un onglet à l'autre et la destination choisie pour les
          coûts ouvrirait d'emblée la grille des budgets. Changer d'onglet n'est pas choisir
          une destination.
        */}
        {chargement ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            <Loader size={22} className="spin" />
          </div>
        ) : (domaine === 'couts' || domaine === 'budget') && mode === 'import' ? (
          /*
            Un seul composant d'import pour les quatre domaines : seul le jeu de colonnes
            change. Les deux domaines par destination lui passent le catalogue des cibles —
            sans lui, aucune ligne ne peut être validée, et l'écran le dit plutôt que d'écrire
            sous une clé inventée qu'aucun bloc du tableau ne relirait.
          */
          <SaisiesImport
            key={`import-${domaine}`}
            saisiesExistantes={domaine === 'couts' ? couts : budgets}
            jeu={domaine}
            cibles={ciblesDeCout}
            onEnregistrer={domaine === 'couts' ? enregistrerCouts : enregistrerBudgets}
            enregistrement={enregistrement}
            erreur={erreur}
          />
        ) : domaine === 'couts' ? (
          <OngletMontantsParCible
            key="couts"
            lignes={couts}
            cibles={ciblesDeCout}
            titreDuChamp="Coût (€)"
            titreDuSelect="Destination du coût"
            aideDeLaCible={aideCout}
            onEnregistrer={enregistrerCouts}
            enregistrement={enregistrement}
            erreur={erreur}
          />
        ) : domaine === 'budget' ? (
          <OngletMontantsParCible
            key="budget"
            lignes={budgets}
            cibles={ciblesDeCout}
            titreDuChamp="Budget (€)"
            titreDuSelect="Destination du budget"
            aideDeLaCible={aideBudget}
            onEnregistrer={enregistrerBudgets}
            enregistrement={enregistrement}
            erreur={erreur}
          />
        ) : mode === 'saisie' ? (
          <GrilleDeSaisie
            saisies={saisies}
            champs={domaineActuel.champs}
            onEnregistrer={enregistrerSaisies}
            enregistrement={enregistrement}
            erreur={erreur}
          />
        ) : (
          <SaisiesImport
            saisiesExistantes={saisies}
            jeu={domaineActuel.cle}
            onEnregistrer={enregistrerSaisies}
            enregistrement={enregistrement}
            erreur={erreur}
          />
        )}
      </div>
    </div>,
    document.body
  );
}

export default SaisiesModal;
